import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, BookOpen, CheckCircle2, Clock, Trophy,
  ChevronRight, Star, Target, Loader2, BookMarked,
  ChevronDown, ChevronUp, RefreshCw, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReadingChallenge = () => {
  const navigate = useNavigate();
  const { stage } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, reading, results
  const [passages, setPassages] = useState([]);
  const [currentPassage, setCurrentPassage] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(null);
  const [expandedCompleted, setExpandedCompleted] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (stage) {
      loadPassages();
      loadProgress();
    }
  }, [stage]);

  const loadPassages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reading-challenge/passages/${stage}`, { headers });
      setPassages(response.data.passages);
    } catch (error) {
      console.error('Error loading passages:', error);
      toast.error('Failed to load reading passages');
    }
    setLoading(false);
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/reading-challenge/progress`, { headers });
      setProgress(response.data);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const startReading = async (passageId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reading-challenge/passage/${passageId}`, { headers });
      setCurrentPassage(response.data);
      setAnswers(response.data.previous_answers || {});
      setView('reading');
    } catch (error) {
      toast.error('Failed to load passage');
    }
    setLoading(false);
  };

  const submitAnswers = async () => {
    if (!currentPassage) return;
    
    // Check if all questions answered
    const unanswered = currentPassage.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} remaining questions`);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/reading-challenge/submit`, {
        passage_id: currentPassage.id,
        answers
      }, { headers });
      
      setResults(response.data);
      setView('results');
      toast.success(`Completed! Score: ${response.data.score_percentage}%`);
      
      // Refresh passages to update completion status
      loadPassages();
      loadProgress();
    } catch (error) {
      toast.error('Failed to submit answers');
    }
    setLoading(false);
  };

  const resetPassage = async (passageId) => {
    try {
      await axios.delete(`${API}/reading-challenge/reset/${passageId}`, { headers });
      toast.success('Passage reset - you can retake it now');
      loadPassages();
      loadProgress();
    } catch (error) {
      toast.error('Failed to reset passage');
    }
  };

  const toggleCompletedExpand = (passageId) => {
    setExpandedCompleted(prev => ({
      ...prev,
      [passageId]: !prev[passageId]
    }));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-500/20 text-green-600 border-green-500/30';
      case 'medium': return 'bg-amber-500/20 text-amber-600 border-amber-500/30';
      case 'hard': return 'bg-red-500/20 text-red-600 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-500';
    }
  };

  const stageName = stage === '7-plus' ? '7+ Entry' : '11+ Entry';

  if (loading && view === 'list') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Results View
  if (view === 'results' && results) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => { setView('list'); setResults(null); setCurrentPassage(null); }}
            className="text-slate-500 hover:text-slate-900 mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Passages
          </Button>

          <Card className="bg-slate-100 border-slate-200/50 mb-6">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-cyan-500/20 mb-4">
                <Trophy className="h-10 w-10 text-cyan-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                {results.score_percentage >= 80 ? 'Excellent!' : results.score_percentage >= 60 ? 'Good Job!' : 'Keep Practicing!'}
              </h2>
              <p className="text-5xl font-bold text-cyan-600 mb-2">{results.score_percentage}%</p>
              <p className="text-slate-500">
                {results.correct_count} of {results.total_questions} questions correct
              </p>
            </CardContent>
          </Card>

          <h3 className="text-xl font-bold text-slate-900 mb-4">Review Your Answers</h3>
          
          <div className="space-y-4">
            {results.results.map((r, idx) => (
              <Card 
                key={r.question_id}
                className={`border ${r.is_correct ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${r.is_correct ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {r.is_correct ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <span className="text-red-600 font-bold">✗</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-900 font-medium mb-2">Q{idx + 1}: {r.question}</p>
                      <p className="text-sm">
                        <span className="text-slate-500">Your answer: </span>
                        <span className={r.is_correct ? 'text-green-600' : 'text-red-600'}>{r.user_answer || '(not answered)'}</span>
                      </p>
                      {!r.is_correct && (
                        <p className="text-sm">
                          <span className="text-slate-500">Correct answer: </span>
                          <span className="text-green-600">{r.correct_answer}</span>
                        </p>
                      )}
                      <p className="text-sm text-slate-500 mt-2 italic">{r.explanation}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Button
              onClick={() => { setView('list'); setResults(null); setCurrentPassage(null); }}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
            >
              Continue Reading Challenge
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Reading View
  if (view === 'reading' && currentPassage) {
    const answeredCount = Object.keys(answers).length;
    const totalQuestions = currentPassage.questions.length;

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => { setView('list'); setCurrentPassage(null); setAnswers({}); }}
            className="text-slate-500 hover:text-slate-900 mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Passages
          </Button>

          {/* Passage Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Badge className={getDifficultyColor(currentPassage.difficulty)}>
                {currentPassage.difficulty}
              </Badge>
              <span className="text-slate-500 text-sm">{currentPassage.word_count} words</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{currentPassage.title}</h1>
          </div>

          {/* Passage Text */}
          <Card className="bg-slate-100 border-slate-200/50 mb-8">
            <CardContent className="p-6 md:p-8">
              <div className="prose prose-invert max-w-none">
                {currentPassage.passage.split('\n\n').map((para, idx) => (
                  <p key={idx} className="text-slate-600 leading-relaxed mb-4 text-base md:text-lg">
                    {para}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Questions Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Target className="h-5 w-5 text-cyan-600" />
                Comprehension Questions
              </h2>
              <span className="text-slate-500 text-sm">
                {answeredCount}/{totalQuestions} answered
              </span>
            </div>
            <Progress value={(answeredCount / totalQuestions) * 100} className="h-2" />
          </div>

          <div className="space-y-6">
            {currentPassage.questions.map((q, idx) => (
              <Card 
                key={q.id}
                className={`border transition-all ${answers[q.id] ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-slate-200/50 bg-slate-100'}`}
              >
                <CardContent className="p-5">
                  <p className="text-slate-900 font-medium mb-4">
                    <span className="text-cyan-600 mr-2">Q{idx + 1}.</span>
                    {q.question}
                  </p>
                  <RadioGroup
                    value={answers[q.id] || ''}
                    onValueChange={(value) => setAnswers({ ...answers, [q.id]: value })}
                  >
                    <div className="grid gap-3">
                      {q.options.map((option, optIdx) => (
                        <div 
                          key={optIdx}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer
                            ${answers[q.id] === option 
                              ? 'border-cyan-500/50 bg-cyan-500/10' 
                              : 'border-slate-200/50 hover:border-slate-200'}`}
                        >
                          <RadioGroupItem value={option} id={`${q.id}-${optIdx}`} />
                          <Label 
                            htmlFor={`${q.id}-${optIdx}`}
                            className="text-slate-600 cursor-pointer flex-1"
                          >
                            {option}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Submit Button */}
          <div className="mt-8 flex justify-center">
            <Button
              onClick={submitAnswers}
              disabled={loading || answeredCount < totalQuestions}
              size="lg"
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 px-8"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-5 w-5 mr-2" />
              )}
              Submit & Complete
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // List View
  const completedPassages = passages.filter(p => p.completed);
  const availablePassages = passages.filter(p => !p.completed);
  const stageProgress = progress?.stages?.[stage] || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
        </div>

        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <BookOpen className="h-4 w-4 text-cyan-600" />
            <span className="text-sm text-cyan-600">Reading Challenge</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            {stageName} Reading
          </h1>
          <p className="text-slate-500">
            Read passages carefully and answer comprehension questions
          </p>
        </div>

        {/* Progress Card */}
        <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/20 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Your Progress</h3>
              <Badge className="bg-cyan-500/20 text-cyan-600">
                {stageProgress.completed_count || 0}/{stageProgress.total_passages || passages.length} Complete
              </Badge>
            </div>
            <Progress value={stageProgress.completion_percentage || 0} className="h-3 mb-3" />
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Average Score</span>
              <span className="text-cyan-600 font-semibold">{stageProgress.average_score || 0}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Available Passages */}
        {availablePassages.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              Available Passages ({availablePassages.length})
            </h2>
            <div className="grid gap-4">
              {availablePassages.map((passage) => (
                <Card 
                  key={passage.id}
                  className="bg-slate-100 border-slate-200/50 hover:border-cyan-500/30 transition-all cursor-pointer group"
                  onClick={() => startReading(passage.id)}
                  data-testid={`passage-card-${passage.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getDifficultyColor(passage.difficulty)}>
                            {passage.difficulty}
                          </Badge>
                          <span className="text-slate-500 text-xs">{passage.word_count} words</span>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                          {passage.title}
                        </h3>
                        <p className="text-slate-500 text-sm mt-1">
                          {passage.question_count} comprehension questions
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        className="text-cyan-600 group-hover:bg-cyan-500/10"
                      >
                        Start
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Completed Passages */}
        {completedPassages.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Completed ({completedPassages.length})
            </h2>
            <div className="grid gap-3">
              {completedPassages.map((passage) => (
                <Card 
                  key={passage.id}
                  className="bg-slate-100 border-slate-200/30"
                >
                  <CardContent className="p-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleCompletedExpand(passage.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="text-slate-900 font-medium">{passage.title}</h3>
                          <p className="text-slate-500 text-sm">
                            Score: <span className="text-green-600">{passage.score}%</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-500/20 text-green-600 text-xs">
                          Complete
                        </Badge>
                        {expandedCompleted[passage.id] ? (
                          <ChevronUp className="h-5 w-5 text-slate-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-500" />
                        )}
                      </div>
                    </div>
                    
                    {expandedCompleted[passage.id] && (
                      <div className="mt-4 pt-4 border-t border-slate-200/50 flex items-center justify-between">
                        <p className="text-slate-500 text-sm">
                          Completed on {new Date(passage.completed_at).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); startReading(passage.id); }}
                            className="border-slate-200 text-slate-600 hover:bg-slate-100"
                          >
                            <BookMarked className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); resetPassage(passage.id); }}
                            className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Retake
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {passages.length === 0 && !loading && (
          <Card className="bg-slate-100 border-slate-200/50">
            <CardContent className="p-8 text-center">
              <BookOpen className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Passages Available</h3>
              <p className="text-slate-500">Check back soon for new reading challenges!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReadingChallenge;
